import { motion } from "framer-motion";
import { MapPin, Clock, Mail } from "lucide-react";
import { T } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon, Phone } from "./icons";
import { BookingQR } from "./BookingQR";

const dayIndex = new Date().getDay(); // 0 Sun .. 6 Sat
const orderMap = [6, 0, 1, 2, 3, 4, 5]; // HOURS array index for JS day

export const Visit = () => {
  const { lang, tr } = useLang();
  const { hours, brand } = useSiteData();
  const todayIdx = orderMap[dayIndex];
  const mapsQuery = brand.address || brand.mapsQuery;
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(mapsQuery)}&z=16&output=embed`;

  return (
    <section id="visit" className="relative bg-[#15100B] py-24 lg:py-36" data-testid="visit-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-14">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.visit.label)}</span>
          </div>
          <h2 className="font-serif text-5xl tracking-tight text-[#F5ECDA] md:text-6xl">{tr(T.visit.title)}</h2>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-sm border border-white/10 bg-[#1C1610] p-8 lg:p-10">
              <div className="mb-5 flex items-center gap-3">
                <Clock className="h-4 w-4 text-[#D4AF37]" />
                <h3 className="text-xs uppercase tracking-[0.25em] text-[#F5ECDA]">{tr(T.visit.hours)}</h3>
              </div>
              <ul>
                {hours.map((h, i) => {
                  const active = i === todayIdx;
                  return (
                    <li
                      key={i}
                      className={`flex items-center justify-between border-b border-white/5 py-3 text-sm ${
                        active ? "text-[#D4AF37]" : "text-[#D9CFB9]"
                      }`}
                      data-testid={`hours-row-${i}`}
                    >
                      <span className="flex items-center gap-2">
                        {active && <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />}
                        {tr(h.day)}
                      </span>
                      <span className={h.appt ? "italic text-[#B7A78C]" : ""}>{tr(h.time)}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
                  <p className="text-sm leading-relaxed text-[#D9CFB9]">{brand.address}</p>
                </div>
                <a href={`tel:${brand.tel}`} className="flex items-center gap-3 text-sm text-[#D9CFB9] transition-colors hover:text-[#D4AF37]" data-testid="visit-phone">
                  <Phone className="h-4 w-4 shrink-0 text-[#D4AF37]" /> {brand.phoneDisplay}
                </a>
                <a href={`mailto:${brand.email}`} className="flex items-center gap-3 text-sm text-[#D9CFB9] transition-colors hover:text-[#D4AF37]" data-testid="visit-email">
                  <Mail className="h-4 w-4 shrink-0 text-[#D4AF37]" /> {brand.email}
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors hover:bg-[#F3E5AB]" data-testid="visit-whatsapp-cta">
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  data-testid="visit-directions-cta"
                >
                  <MapPin className="h-4 w-4" /> {tr(T.visit.directions)}
                </a>
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                <BookingQR />
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative min-h-[400px] overflow-hidden rounded-sm border border-white/10 lg:col-span-7"
          >
            <iframe
              title="Slaly Hair & Beauty location"
              src={mapSrc}
              className="map-frame absolute inset-0 h-full w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-iframe"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
